import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { useNavigate } from 'react-router-dom';
import { UserPlus } from 'lucide-react';
import { cn } from '../lib/utils';
import { useAuth } from '../hooks/useAuth';

interface FriendSuggestion {
  id: string;
  full_name: string;
  avatar_url: string | null;
  mutual_friends: number;
}

interface FriendSuggestionsProps {
  onFriendRequestSent?: () => void;
}

export default function FriendSuggestions({ onFriendRequestSent }: FriendSuggestionsProps) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [suggestions, setSuggestions] = useState<FriendSuggestion[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sendingRequest, setSendingRequest] = useState<Record<string, boolean>>({});
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    if (user) {
      loadSuggestions();
    }
  }, [user]);

  const loadSuggestions = async () => {
    if (!user) return;

    try {
      setLoading(true);
      setError(null);

      const { data, error } = await supabase
        .rpc('get_friend_suggestions', {
          user_id: user.id,
          suggestion_limit: showAll ? 100 : 6
        });

      if (error) {
        console.error('RPC Error:', error);
        throw new Error('Failed to load suggestions');
      }

      setSuggestions(data || []);
    } catch (err) {
      console.error('Error loading friend suggestions:', err);
      setError('Failed to load suggestions. Please try again.');
      setSuggestions([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSendRequest = async (userId: string) => {
    if (!user) return;

    try {
      setSendingRequest(prev => ({ ...prev, [userId]: true }));
      setError(null);

      // Check if a request already exists
      const { data: existingRequest, error: checkError } = await supabase
        .from('friends')
        .select('id, status')
        .or(`and(sender_id.eq.${user.id},receiver_id.eq.${userId}),and(sender_id.eq.${userId},receiver_id.eq.${user.id})`)
        .maybeSingle();

      if (checkError) throw checkError;

      if (existingRequest) {
        throw new Error('A friend request already exists');
      }

      const { error: insertError } = await supabase
        .from('friends')
        .insert({
          sender_id: user.id,
          receiver_id: userId,
          status: 'pending'
        });

      if (insertError) throw insertError;

      // Remove the suggestion after sending request
      setSuggestions(prev => prev.filter(s => s.id !== userId));
      
      // Notify parent component that a request was sent
      onFriendRequestSent?.();
    } catch (err: any) {
      console.error('Error sending friend request:', err);
      setError(err.message || 'Failed to send friend request. Please try again.');
    } finally {
      setSendingRequest(prev => ({ ...prev, [userId]: false }));
    }
  };

  const getAvatarUrl = (suggestion: FriendSuggestion) => {
    if (!suggestion.avatar_url) {
      return `https://api.dicebear.com/7.x/initials/svg?seed=${suggestion.full_name}`;
    }

    if (suggestion.avatar_url.startsWith('http')) {
      return suggestion.avatar_url;
    }

    const { data } = supabase.storage.from('avatars').getPublicUrl(suggestion.avatar_url);
    return data.publicUrl;
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-6 bg-gray-200 rounded w-1/4"></div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="flex items-center space-x-3 p-4 bg-gray-100 rounded-lg">
                <div className="h-12 w-12 rounded-full bg-gray-200"></div>
                <div className="flex-1 space-y-2">
                  <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                  <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                </div>
                <div className="h-8 w-8 rounded-full bg-gray-200"></div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="text-center">
          <p className="text-red-500 mb-4">{error}</p>
          <button
            onClick={loadSuggestions}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-500 rounded-lg hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (suggestions.length === 0) {
    return null;
  }

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-semibold text-gray-900">People You Might Know</h2>
        {suggestions.length > 6 && !showAll && (
          <button
            onClick={() => {
              setShowAll(true);
              loadSuggestions();
            }}
            className="text-blue-600 hover:text-blue-800 text-sm font-medium"
          >
            See All
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {suggestions.slice(0, showAll ? undefined : 6).map(suggestion => (
          <div
            key={suggestion.id}
            className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <div 
              className="flex items-center space-x-3 flex-1 min-w-0 cursor-pointer"
              onClick={() => navigate(`/user/${suggestion.id}`)}
            >
              <img
                src={getAvatarUrl(suggestion)}
                alt={suggestion.full_name}
                className="h-12 w-12 rounded-full object-cover bg-gray-200"
                onError={(e) => {
                  const img = e.target as HTMLImageElement;
                  img.src = `https://api.dicebear.com/7.x/initials/svg?seed=${suggestion.full_name}`;
                }}
              />
              <div className="flex-1 min-w-0">
                <p className="font-medium text-gray-900 truncate">
                  {suggestion.full_name}
                </p>
                <p className="text-sm text-gray-500">
                  {suggestion.mutual_friends} {suggestion.mutual_friends === 1 ? 'friend' : 'friends'} in common
                </p>
              </div>
            </div>
            
            <button
              onClick={() => handleSendRequest(suggestion.id)}
              disabled={sendingRequest[suggestion.id]}
              className={cn(
                "ml-4 p-2 rounded-full text-blue-600 hover:bg-blue-50 transition-colors",
                sendingRequest[suggestion.id] && "opacity-50 cursor-not-allowed"
              )}
              title="Send friend request"
            >
              <UserPlus className="h-5 w-5" />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}